import React, { useEffect, useMemo, useState } from "react";
import {
  createTokenListing,
  updateTokenListing,
  lookupTokenListing,
} from "../../../helpers/fakebackend_helper";
import { CHAINS, CATEGORIES, PLACEMENTS, MAX_SPONSOR_DAYS, NOTES_MAX } from "../constants";
import {
  emptyForm,
  listingToForm,
  validateForm,
  formToPayload,
  diffPayload,
  legacyPlacementRetry,
  sponsorshipChanged,
  sponsorshipState,
  describeError,
  isEvmChain,
  normaliseAddress,
} from "../utils";
import Panel, { btnPrimary, btnGhost, inputCls, labelCls, sectionCls, sectionTitleCls } from "./Panel";
import LogoField from "./LogoField";
import { SponsorBadge, StatusBadge } from "./Badges";

const FieldError = ({ msg }) => (msg ? <div className="text-xs text-red-300 mt-1">{msg}</div> : null);

const Toggle = ({ id, checked, onChange, label, hint, disabled }) => (
  <label htmlFor={id} className="flex items-start gap-3 cursor-pointer select-none">
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="mt-0.5 h-4 w-4 accent-[#5758FC]"
    />
    <span>
      <span className="text-sm text-gray-100">{label}</span>
      {hint && <span className="block text-xs text-gray-400">{hint}</span>}
    </span>
  </label>
);

/**
 * Add / edit a token listing. Create mode can be prefilled (for example from a
 * form submission). Edit mode sends only the changed fields; chain and address
 * are the listing's identity and stay locked.
 */
export default function ListingForm({
  open,
  mode = "create",
  listing = null,
  initialForm = null,
  hints = [],
  onClose,
  onSaved,
  onOpenExisting,
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [lookup, setLookup] = useState({ busy: false, note: "", existingId: null, tone: "info" });

  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? listingToForm(listing) : { ...emptyForm(), ...(initialForm || {}) });
    setErrors({});
    setFormError("");
    setLookup({ busy: false, note: "", existingId: null, tone: "info" });
  }, [open, isEdit, listing, initialForm]);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };
  const setInput = (key) => (e) => set(key)(e.target.value);

  const toggleIn = (key, value) => {
    setForm((f) => {
      const list = Array.isArray(f[key]) ? f[key] : [];
      return { ...f, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const clearSponsorship = () => {
    setForm((f) => ({ ...f, sponsor_name: "", sponsored_from: "", sponsored_until: "", sponsored_placements: [] }));
    setErrors((e) => ({ ...e, sponsor_name: undefined, sponsored_from: undefined, sponsored_until: undefined, sponsored_placements: undefined }));
  };

  const canLookup =
    !lookup.busy &&
    ((form.network_id !== "" && String(form.contract_address).trim()) || String(form.coingecko_id).trim());

  const runLookup = async () => {
    const addr = String(form.contract_address).trim();
    const body =
      form.network_id !== "" && addr
        ? { network_id: Number(form.network_id), contract_address: normaliseAddress(form.network_id, addr) }
        : { coingecko_id: String(form.coingecko_id).trim() };
    setLookup({ busy: true, note: "", existingId: null, tone: "info" });
    try {
      const res = await lookupTokenListing(body);
      const existingId = res && res.existing_listing_id ? res.existing_listing_id : null;
      const s = (res && res.suggestion) || {};
      if (res && res.found) {
        // Fill only empty fields so nothing the user typed is overwritten.
        setForm((f) => ({
          ...f,
          name: f.name || (s.name ? String(s.name).slice(0, 80) : ""),
          symbol: f.symbol || (s.symbol ? String(s.symbol).slice(0, 20) : ""),
          decimals: f.decimals !== "" ? f.decimals : s.decimals !== undefined && s.decimals !== null ? String(s.decimals) : "",
          logo_url: f.logo_url || (typeof s.logo_url === "string" && /^https:\/\//i.test(s.logo_url) ? s.logo_url : ""),
          coingecko_id: f.coingecko_id || (s.coingecko_id ? String(s.coingecko_id) : ""),
        }));
      }
      if (existingId && (!isEdit || String(existingId) !== String(listing && listing.id))) {
        setLookup({ busy: false, existingId, tone: "warn", note: `This token is already listed (listing ${existingId}).` });
      } else if (res && res.found) {
        setLookup({ busy: false, existingId: null, tone: "info", note: "Empty fields were filled from the catalogue. Check them before saving." });
      } else {
        setLookup({ busy: false, existingId: null, tone: "info", note: "No match in the catalogue or on CoinGecko. Fill the fields by hand." });
      }
    } catch (err) {
      setLookup({ busy: false, existingId: null, tone: "warn", note: describeError(err, "The lookup could not be completed. Fill the fields by hand.") });
    }
  };

  const original = isEdit ? listing : null;
  const sponsorWillReset = useMemo(
    () => Boolean(isEdit && original && original.sponsored_approved && sponsorshipChanged(form, original)),
    [isEdit, original, form],
  );

  const submit = async () => {
    setFormError("");
    const v = validateForm(form, { isEdit });
    const clean = Object.fromEntries(Object.entries(v).filter(([, m]) => m));
    setErrors(clean);
    if (Object.keys(clean).length) {
      setFormError("Fix the highlighted fields.");
      return;
    }
    let body;
    if (isEdit) {
      body = diffPayload(form, original);
      if (Object.keys(body).length === 0) {
        setFormError("No changes to save.");
        return;
      }
    } else {
      body = formToPayload(form);
    }
    setSaving(true);
    try {
      const send = (b) => (isEdit ? updateTokenListing(original.id, b) : createTokenListing(b));
      let res;
      try {
        res = await send(body);
      } catch (e) {
        // an API older than the placement rename: send the same choice in its keys
        const retry = legacyPlacementRetry(e, body);
        if (!retry) throw e;
        res = await send(retry);
      }
      if (onSaved) onSaved((res && res.listing) || null, mode);
    } catch (err) {
      const existing = err && err.data && err.data.existing_listing_id;
      if (existing) setLookup({ busy: false, existingId: existing, tone: "warn", note: `This token is already listed (listing ${existing}).` });
      setFormError(describeError(err, isEdit ? "The listing could not be saved. Try again." : "The listing could not be created. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? `Edit ${listing && listing.symbol ? listing.symbol : "listing"}` : "Add token listing";
  const evm = isEvmChain(form.network_id);
  const sponsorState = isEdit ? sponsorshipState(original) : "none";

  return (
    <Panel
      open={open}
      onClose={onClose}
      busy={saving}
      labelId="tl-form-title"
      title={title}
      subtitle={
        isEdit && original ? (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <StatusBadge status={original.status} />
            <span>Listing {original.id}</span>
            {original.updated_by && <span>Last edited by {original.updated_by}</span>}
          </span>
        ) : (
          "New listings start as a draft unless you choose Active."
        )
      }
      footer={
        <>
          {formError && <div className="mr-auto text-sm text-red-300" role="alert">{formError}</div>}
          <button type="button" className={btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save changes" : "Create listing"}
          </button>
        </>
      }
    >
      {hints.length > 0 && (
        <div className="p-3 rounded-md border border-bl-accent/30 bg-bl-accent/10 text-sm text-gray-200 space-y-1">
          {hints.map((h) => <div key={h}>{h}</div>)}
        </div>
      )}

      <section className={sectionCls}>
        <h4 className={sectionTitleCls}>Token</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="tl-chain">Chain</label>
            <select id="tl-chain" value={form.network_id} onChange={setInput("network_id")} className={inputCls} disabled={isEdit} aria-invalid={Boolean(errors.network_id)}>
              <option value="">Choose a chain</option>
              {CHAINS.map((c) => (
                <option key={c.network_id} value={String(c.network_id)}>{c.name}</option>
              ))}
            </select>
            <FieldError msg={errors.network_id} />
          </div>
          <div>
            <label className={labelCls} htmlFor="tl-address">Contract address</label>
            <input
              id="tl-address"
              type="text"
              value={form.contract_address}
              onChange={setInput("contract_address")}
              placeholder={evm ? "0x... or native" : "Address, or native"}
              className={`${inputCls} font-mono`}
              disabled={isEdit}
              spellCheck={false}
              autoComplete="off"
              aria-invalid={Boolean(errors.contract_address)}
            />
            <FieldError msg={errors.contract_address} />
            {isEdit && <div className="text-xs text-gray-500 mt-1">Chain and address identify the listing and cannot change.</div>}
          </div>
          <div>
            <label className={labelCls} htmlFor="tl-cg">CoinGecko id</label>
            <input id="tl-cg" type="text" value={form.coingecko_id} onChange={setInput("coingecko_id")} placeholder="optional, for example usd-coin" className={inputCls} spellCheck={false} autoComplete="off" aria-invalid={Boolean(errors.coingecko_id)} />
            <FieldError msg={errors.coingecko_id} />
          </div>
          <div className="flex items-end">
            <button type="button" className={btnGhost} onClick={runLookup} disabled={!canLookup}>
              {lookup.busy ? "Looking up..." : "Look up"}
            </button>
            <span className="ml-3 text-xs text-gray-500">Prefills name, symbol, decimals and logo.</span>
          </div>
        </div>
        {lookup.note && (
          <div className={`mt-3 text-sm ${lookup.tone === "warn" ? "text-amber-300" : "text-gray-300"}`} role="status">
            {lookup.note}
            {lookup.existingId && onOpenExisting && (
              <button type="button" className="ml-2 underline text-bl-accent-light" onClick={() => onOpenExisting(lookup.existingId)}>
                Open it
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="tl-name">Name</label>
            <input id="tl-name" type="text" value={form.name} onChange={setInput("name")} maxLength={80} className={inputCls} aria-invalid={Boolean(errors.name)} />
            <FieldError msg={errors.name} />
          </div>
          <div>
            <label className={labelCls} htmlFor="tl-symbol">Symbol</label>
            <input id="tl-symbol" type="text" value={form.symbol} onChange={setInput("symbol")} maxLength={20} className={inputCls} aria-invalid={Boolean(errors.symbol)} />
            <FieldError msg={errors.symbol} />
          </div>
          <div>
            <label className={labelCls} htmlFor="tl-decimals">Decimals</label>
            <input id="tl-decimals" type="number" min={0} max={36} step={1} value={form.decimals} onChange={setInput("decimals")} className={inputCls} aria-invalid={Boolean(errors.decimals)} />
            <FieldError msg={errors.decimals} />
          </div>
        </div>

        <div className="mt-4">
          <LogoField value={form.logo_url} symbol={form.symbol} onChange={set("logo_url")} error={errors.logo_url} disabled={saving} />
        </div>
      </section>

      <section className={sectionCls}>
        <h4 className={sectionTitleCls}>Placement in the app</h4>
        {!isEdit && (
          <div className="mb-4">
            <span className={labelCls}>Status</span>
            <div className="flex gap-4">
              {[
                { v: "draft", label: "Draft", hint: "Saved, not shown in the app" },
                { v: "active", label: "Active", hint: "Shown in search and market lists" },
              ].map((o) => (
                <label key={o.v} className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="tl-status" value={o.v} checked={form.status === o.v} onChange={() => set("status")(o.v)} className="mt-0.5 accent-[#5758FC]" />
                  <span>
                    <span className="text-sm text-gray-100">{o.label}</span>
                    <span className="block text-xs text-gray-400">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-3">
          <Toggle id="tl-verified" checked={form.verified} onChange={set("verified")} label="Verified" hint="Shows the Verified mark next to the token." />
          <div className="flex items-start gap-4 flex-wrap">
            <Toggle id="tl-featured" checked={form.featured} onChange={set("featured")} label="Featured" hint="Pinned in the Featured row on Home, under Explore." />
            {form.featured && (
              <div className="w-32">
                <label className={labelCls} htmlFor="tl-rank">Rank</label>
                <input id="tl-rank" type="number" min={0} step={1} value={form.featured_rank} onChange={setInput("featured_rank")} placeholder="1 = first" className={inputCls} aria-invalid={Boolean(errors.featured_rank)} />
                <FieldError msg={errors.featured_rank} />
              </div>
            )}
          </div>
        </div>
        <div className="mt-4">
          <span className={labelCls}>Categories</span>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const on = form.categories.includes(c.slug);
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => toggleIn("categories", c.slug)}
                  aria-pressed={on}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${on ? "border-bl-accent/60 bg-bl-accent/20 text-gray-100" : "border-white/10 text-gray-400 hover:text-gray-200"}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
          <FieldError msg={errors.categories} />
        </div>
      </section>

      <section className={sectionCls}>
        <div className="flex items-center justify-between mb-4 gap-2">
          <h4 className={`${sectionTitleCls} mb-0`}>Sponsorship</h4>
          <div className="flex items-center gap-2">
            {isEdit && <SponsorBadge state={sponsorState} />}
            <button type="button" className="text-xs text-gray-400 hover:text-gray-200" onClick={clearSponsorship}>Clear</button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Optional. A campaign runs for at most {MAX_SPONSOR_DAYS} days and appears in the app only after the core team approves it.
          Times are in your local time zone.
        </p>
        {sponsorWillReset && (
          <div className="mb-4 p-3 rounded-md border border-amber-400/30 bg-amber-400/10 text-sm text-amber-200">
            Saving this change clears the current approval. The campaign will need to be approved again.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3">
            <label className={labelCls} htmlFor="tl-sponsor">Sponsor name</label>
            <input id="tl-sponsor" type="text" value={form.sponsor_name} onChange={setInput("sponsor_name")} maxLength={80} className={inputCls} aria-invalid={Boolean(errors.sponsor_name)} />
            <FieldError msg={errors.sponsor_name} />
          </div>
          <div className="sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="tl-from">From</label>
              <input id="tl-from" type="datetime-local" value={form.sponsored_from} onChange={setInput("sponsored_from")} className={inputCls} aria-invalid={Boolean(errors.sponsored_from)} />
              <FieldError msg={errors.sponsored_from} />
            </div>
            <div>
              <label className={labelCls} htmlFor="tl-until">Until</label>
              <input id="tl-until" type="datetime-local" value={form.sponsored_until} onChange={setInput("sponsored_until")} className={inputCls} aria-invalid={Boolean(errors.sponsored_until)} />
              <FieldError msg={errors.sponsored_until} />
            </div>
          </div>
          <div className="sm:col-span-3">
            <span className={labelCls}>Placements</span>
            <div className="flex gap-5 flex-wrap">
              {PLACEMENTS.map((p) => (
                <label key={p.value} className="flex items-center gap-2 text-sm text-gray-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.sponsored_placements.includes(p.value)}
                    onChange={() => toggleIn("sponsored_placements", p.value)}
                    className="h-4 w-4 accent-[#5758FC]"
                  />
                  <span>
                    {p.label}
                    {p.hint && <span className="block text-xs text-gray-400">{p.hint}</span>}
                  </span>
                </label>
              ))}
            </div>
            <FieldError msg={errors.sponsored_placements} />
          </div>
        </div>
      </section>

      <section className={sectionCls}>
        <h4 className={sectionTitleCls}>Internal notes</h4>
        <textarea
          value={form.notes}
          onChange={setInput("notes")}
          rows={4}
          maxLength={NOTES_MAX}
          placeholder="Not shown in the app. For example: partnership allocation sent to treasury."
          className={`${inputCls} resize-y`}
          aria-label="Internal notes"
          aria-invalid={Boolean(errors.notes)}
        />
        <div className="text-xs text-gray-500 mt-1">{form.notes.length} / {NOTES_MAX}</div>
        <FieldError msg={errors.notes} />
        {isEdit && original && original.delist_reason && (
          <div className="text-xs text-gray-400 mt-3">Delist reason: {original.delist_reason}</div>
        )}
      </section>
    </Panel>
  );
}
